import React from 'react';
import { Box, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ProductInfo } from '../compareData';
import { GRAY, INK, MOBILE, SPOTLIGHT, TABLET } from '../constants';

interface ProductCardProps {
  product: ProductInfo;
  /** Currently shown render (swatch-selected for S3, otherwise the default image). */
  image: string;
  /** Present only for products with collar-color swatches (S3). */
  onSelectColor?: (image: string) => void;
}

/** Product column: spotlight thumbnail (+ optional color selector), name, tagline, description. */
export const ProductCard: React.FC<ProductCardProps> = ({ product, image, onSelectColor }) => {
  const { t } = useTranslation();
  const hasSwatches = Boolean(product.swatches);
  return (
    <Box sx={{ padding: '22px 20px', display: 'flex', flexDirection: 'column', [MOBILE]: { padding: 14 } }}>
      <Box
        sx={{
          position: 'relative',
          background: SPOTLIGHT,
          borderRadius: 12,
          height: 210,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          paddingBottom: hasSwatches ? 46 : 16,
          '& img': { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' },
          [TABLET]: { height: 190 },
          [MOBILE]: { height: 150, borderRadius: 10, padding: 10, paddingBottom: hasSwatches ? 32 : 10 },
        }}
      >
        <img src={image} alt={t(product.nameKey)} />
        {product.swatches && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 14,
              display: 'flex',
              justifyContent: 'center',
              [MOBILE]: { bottom: 10 },
            }}
          >
            <Box sx={{ display: 'flex', gap: 10 }}>
              {product.swatches.map((s) => (
                <UnstyledButton
                  key={s.image}
                  type="button"
                  aria-label={t('compare_v2_select_color')}
                  onClick={() => onSelectColor?.(s.image)}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 9999,
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    padding: 0,
                    cursor: 'pointer',
                    background: s.color,
                    transition: 'box-shadow 0.12s ease',
                    boxShadow: image === s.image ? '0 0 0 2px #fff, 0 0 0 3.5px #000' : 'none',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <Box fw={600} lts="-0.2px" sx={{ fontSize: 20, marginTop: 16, [MOBILE]: { fontSize: 13, marginTop: 10 } }}>
        {t(product.nameKey)}
      </Box>
      <Box
        fw={700}
        c={INK}
        lts="-0.5px"
        sx={{ fontSize: 23, lineHeight: 1.14, marginTop: 8, [MOBILE]: { fontSize: 15, lineHeight: 1.2, marginTop: 6 } }}
      >
        {t(product.taglineKey)}
      </Box>
      <Box
        c={GRAY}
        lh={1.4}
        sx={{
          fontSize: 15,
          marginTop: 12,
          minHeight: 42,
          [MOBILE]: { fontSize: 12, lineHeight: 1.35, marginTop: 8, minHeight: 48 },
        }}
      >
        {t(product.descKey)}
      </Box>
    </Box>
  );
};
